/* Everwell motion engine — scroll reveals, hero split, tilt, parallax, count-up */
(function () {
  'use strict';

  /* Reduced motion disables autoplaying effects (reveals, parallax, tilt,
     count-up, auto-rotate) but keeps user-initiated interaction (drag-to-spin)
     working - see per-feature gating in the DOMContentLoaded handler. */
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Auto-tag reveal targets ---------- */
  var REVEAL_GROUPS = [
    { sel: '.section-header', cls: 'reveal' },
    { sel: '.pillar', cls: 'reveal', stagger: true },
    { sel: '.testimonial', cls: 'reveal', stagger: true },
    { sel: '.coming-card', cls: 'reveal reveal--scale', stagger: true },
    { sel: '.spec-item', cls: 'reveal reveal--left', stagger: true },
    { sel: '.proof-item', cls: 'reveal', stagger: true },
    { sel: '.product-card', cls: 'reveal', stagger: true },
    { sel: '.article-card', cls: 'reveal', stagger: true },
    { sel: '.faq-item', cls: 'reveal', stagger: true },
    { sel: '.feature-media', cls: 'reveal reveal--left' },
    { sel: '.feature-grid > div:last-child', cls: 'reveal reveal--right' },
    { sel: '.story-banner__content', cls: 'reveal reveal--left' },
    { sel: '.cult .h1, .cult__sub, .cult-form', cls: 'reveal', stagger: true }
  ];

  function setupReveals() {
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    REVEAL_GROUPS.forEach(function (group) {
      var els = document.querySelectorAll(group.sel);
      els.forEach(function (el, i) {
        // Skip if already in initial viewport hero area handled by CSS animations
        group.cls.split(' ').forEach(function (c) { el.classList.add(c); });
        if (group.stagger) el.style.setProperty('--reveal-delay', (i % 8) * 0.09 + 's');
        observer.observe(el);
      });
    });
  }

  /* ---------- Hero heading line splitter ---------- */
  function splitHero() {
    document.querySelectorAll('.hero__heading').forEach(function (h) {
      if (h.querySelector('.hero-line')) return; // already rendered server-side
      var html = h.innerHTML;
      var lines = html.split(/<br\s*\/?>/i);
      if (lines.length === 0) return;
      h.innerHTML = lines.map(function (line, i) {
        return '<span class="hero-line"><span style="--line-i:' + i + '">' + line + '</span></span>';
      }).join('');
    });
  }

  /* ---------- Parallax ---------- */
  function setupParallax() {
    var targets = document.querySelectorAll('.hero__media, .story-banner__media');
    if (!targets.length) return;
    var ticking = false;
    function update() {
      targets.forEach(function (el) {
        var rect = el.parentElement.getBoundingClientRect();
        var vh = window.innerHeight;
        if (rect.bottom < 0 || rect.top > vh) return;
        var progress = (rect.top + rect.height / 2 - vh / 2) / vh; // -1..1
        el.style.setProperty('--parallax-y', (progress * -42).toFixed(1) + 'px');
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- 3D tilt on pointer ---------- */
  function setupTilt() {
    if (window.matchMedia('(hover: none)').matches) return; // skip touch
    var targets = document.querySelectorAll('.feature-media, .product-card, .pdp__image, .coming-card');
    targets.forEach(function (el) {
      el.classList.add('tilt');
      var img = el.querySelector('img');
      if (img) img.classList.add('tilt-pop');
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width;
        var py = (e.clientY - r.top) / r.height;
        el.style.setProperty('--tilt-y', ((px - 0.5) * 10).toFixed(2) + 'deg');
        el.style.setProperty('--tilt-x', ((0.5 - py) * 10).toFixed(2) + 'deg');
        el.style.setProperty('--glare-x', (px * 100).toFixed(1) + '%');
        el.style.setProperty('--glare-y', (py * 100).toFixed(1) + '%');
        el.style.setProperty('--glare-o', '1');
      });
      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--tilt-x', '0deg');
        el.style.setProperty('--tilt-y', '0deg');
        el.style.setProperty('--glare-o', '0');
      });
    });
  }

  /* ---------- Proof strip count-up ---------- */
  function setupCountUp() {
    var items = document.querySelectorAll('.proof-item__value');
    if (!items.length) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        var el = entry.target;
        var match = el.textContent.match(/^(\d+(?:\.\d+)?)/);
        if (!match) return;
        var target = parseFloat(match[1]);
        if (target <= 1) return;
        var decimals = (match[1].split('.')[1] || '').length;
        var html = el.innerHTML;
        var start = null;
        function step(ts) {
          if (!start) start = ts;
          var p = Math.min((ts - start) / 1100, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          var current = (target * eased).toFixed(decimals);
          el.innerHTML = html.replace(match[1], current);
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.6 });
    items.forEach(function (el) { observer.observe(el); });
  }

  /* ---------- Cart count pop ---------- */
  function setupCartPop() {
    var count = document.querySelector('[data-cart-count]');
    if (!count) return;
    new MutationObserver(function () {
      count.classList.remove('pop');
      void count.offsetWidth; // restart animation
      count.classList.add('pop');
    }).observe(count, { childList: true, characterData: true, subtree: true });
  }

  /* ---------- Spinning product bag: auto-rotate + drag with momentum ---------- */
  function setupBagSpin() {
    document.querySelectorAll('.bag-spin').forEach(function (root) {
      var inner = root.querySelector('.bag-spin__inner');
      if (!inner) return;
      var rot = -18;          // start angled so depth reads immediately
      var autoVel = reduced ? 0 : 0.35; // no autoplay under reduced motion; drag still works
      var vel = autoVel;
      var dragging = false;
      var lastX = 0;
      var resumeTimer = null;
      var running = false;

      function frame() {
        if (!dragging) {
          rot += vel;
          // decay drag momentum back toward auto speed
          vel += (autoVel - vel) * 0.02;
        }
        inner.style.transform = 'rotateY(' + rot + 'deg)';
        requestAnimationFrame(frame);
      }

      root.addEventListener('pointerdown', function (e) {
        dragging = true;
        lastX = e.clientX;
        clearTimeout(resumeTimer);
        try { root.setPointerCapture(e.pointerId); } catch (err) {}
      });
      root.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - lastX;
        lastX = e.clientX;
        rot += dx * 0.55;
        vel = dx * 0.55; // momentum carries the release velocity
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
        root.addEventListener(ev, function () { dragging = false; });
      });

      // Only animate while on screen
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !running) {
            running = true;
            requestAnimationFrame(frame);
          }
        });
      }, { rootMargin: '200px' }).observe(root);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!reduced) {
      splitHero();
      setupReveals();
      setupParallax();
      setupTilt();
      setupCountUp();
    }
    setupCartPop();
    setupBagSpin();
  });
})();
